<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { until } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { isVoiceCallEnabled } from 'dashboard/helper/inbox';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useCallHistoryStore } from 'dashboard/stores/callHistory';

import CallListItem from 'dashboard/components-next/Calls/CallListItem.vue';
import CallsEmptyState from 'dashboard/components-next/Calls/CallsEmptyState.vue';
import CallsFilterBar from 'dashboard/components-next/Calls/CallsFilterBar.vue';
import { CALL_ACTIVITY_PARAMS } from 'dashboard/components-next/Calls/constants';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const RESULTS_PER_PAGE = 25;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const callHistoryStore = useCallHistoryStore();

const inboxes = useMapGetter('inboxes/getInboxes');
const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const agents = useMapGetter('agents/getVerifiedAgents');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// CallFinder scopes non-admins to their own accepted calls, so the assignee
// filter is only meaningful for admins; everyone else defaults to themselves.
const { isAdmin } = useAdmin();

const voiceInboxes = computed(() => inboxes.value.filter(isVoiceCallEnabled));

const isVoiceEnabled = computed(
  () =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CHANNEL_VOICE
    ) && voiceInboxes.value.length > 0
);

const calls = computed(() => callHistoryStore.records);
const meta = computed(() => callHistoryStore.meta);
const isFetching = computed(() => callHistoryStore.uiFlags.isFetching);
const accountUiFlags = useMapGetter('accounts/getUIFlags');

const isInitializing = ref(true);

// Filters are seeded from the URL so a shared link restores the same view.
const activity = ref(
  CALL_ACTIVITY_PARAMS[route.query.activity] ? route.query.activity : null
);

const assigneeId = ref(
  isAdmin.value ? Number(route.query.assignee_id) || null : currentUserId.value
);
const inboxId = ref(Number(route.query.inbox_id) || null);
const currentPage = ref(Number(route.query.page) || 1);

const syncFiltersToUrl = () => {
  router.replace({
    query: {
      ...(activity.value && { activity: activity.value }),
      ...(isAdmin.value &&
        assigneeId.value && { assignee_id: assigneeId.value }),
      ...(inboxId.value && { inbox_id: inboxId.value }),
      ...(currentPage.value > 1 && { page: currentPage.value }),
    },
  });
};

const fetchCalls = async () => {
  syncFiltersToUrl();
  try {
    await callHistoryStore.fetchCalls({
      page: currentPage.value,
      ...(CALL_ACTIVITY_PARAMS[activity.value] || {}),
      ...(assigneeId.value ? { agent_id: assigneeId.value } : {}),
      ...(inboxId.value ? { inbox_id: inboxId.value } : {}),
    });
  } catch (error) {
    useAlert(error.message);
  }
};

watch([activity, assigneeId, inboxId], () => {
  currentPage.value = 1;
  fetchCalls();
});

const onPageChange = page => {
  currentPage.value = page;
  fetchCalls();
};

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      until(() => accountUiFlags.value.isFetchingItem).toBe(false),
    ]);
    if (!isVoiceEnabled.value) return;
    // Only admins see the assignee filter, so only they need the agent list.
    if (isAdmin.value) store.dispatch('agents/get');
    await fetchCalls();
  } finally {
    isInitializing.value = false;
  }
});
</script>

<template>
  <div
    v-if="isInitializing"
    class="flex h-full w-full items-center justify-center bg-auth-canvas dark:bg-auth-canvas-dark"
  >
    <Spinner :size="24" class="text-auth-action dark:text-auth-accent" />
  </div>
  <CallsEmptyState v-else-if="!isVoiceEnabled" />
  <section
    v-else
    class="flex h-full w-full flex-col overflow-hidden bg-auth-canvas text-auth-text dark:bg-auth-canvas-dark dark:text-auth-text-dark"
  >
    <header
      class="sticky top-0 z-20 shrink-0 border-b border-auth-border bg-auth-canvas/95 px-3 dark:border-auth-border-dark dark:bg-auth-canvas-dark/95 sm:px-5 lg:px-6"
    >
      <div class="mx-auto w-full max-w-6xl py-4 sm:py-5">
        <h1
          class="text-xl font-semibold tracking-[-0.025em] text-auth-text dark:text-auth-text-dark sm:text-2xl"
        >
          {{ t('CALLS_PAGE.HEADER') }}
        </h1>
        <CallsFilterBar
          v-model:activity="activity"
          v-model:assignee-id="assigneeId"
          v-model:inbox-id="inboxId"
          class="mt-4"
          :total-count="isFetching ? null : meta.count"
          :agents="agents"
          :inboxes="voiceInboxes"
          :show-assignee="isAdmin"
        />
      </div>
    </header>
    <main class="flex-1 overflow-y-auto px-3 sm:px-5 lg:px-6">
      <div class="mx-auto w-full max-w-6xl pb-6 pt-3 sm:pt-4">
        <div
          v-if="isFetching"
          class="flex min-h-64 items-center justify-center rounded-xl border border-auth-border bg-auth-surface dark:border-auth-border-dark dark:bg-auth-surface-dark"
        >
          <Spinner :size="24" class="text-auth-action dark:text-auth-accent" />
        </div>
        <div
          v-else-if="!calls.length"
          class="flex min-h-64 items-center justify-center rounded-xl border border-dashed border-auth-border bg-auth-surface/60 px-6 py-12 text-center dark:border-auth-border-dark dark:bg-auth-surface-dark/60"
        >
          <span class="text-sm text-auth-muted dark:text-auth-muted-dark">
            {{ t('CALLS_PAGE.EMPTY_STATE') }}
          </span>
        </div>
        <div
          v-else
          class="divide-y divide-auth-border overflow-hidden rounded-xl border border-auth-border bg-auth-surface dark:divide-auth-border-dark dark:border-auth-border-dark dark:bg-auth-surface-dark"
        >
          <CallListItem v-for="call in calls" :key="call.id" :call="call" />
        </div>
      </div>
    </main>
    <footer
      v-if="calls.length"
      class="sticky bottom-0 z-10 shrink-0 border-t border-auth-border bg-auth-canvas/95 px-3 dark:border-auth-border-dark dark:bg-auth-canvas-dark/95 sm:px-5 lg:px-6"
    >
      <PaginationFooter
        :current-page="currentPage"
        :total-items="meta.count"
        :items-per-page="RESULTS_PER_PAGE"
        class="mx-auto max-w-6xl !border-0 !bg-transparent !px-0 before:!hidden"
        @update:current-page="onPageChange"
      />
    </footer>
  </section>
</template>
